import React, {
    useState,
    useEffect,
    useMemo,
    useRef,
    useCallback,
} from "react";
import { Breadcrumb } from "../../components/ui/Breadcrumb";
import VizFilters from "./vizFilters";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import {
    getSelectedIsfOrganizationIds,
    isParameterEnabled,
    getPageReferrerTitle,
    getAllowedPoliciesAndReportingGroups,
    filterLeaveCoverageTypeOption,
    isUserEnabledForFlag,
} from "../../utils/helpers";
import {
    CONVERGE_TYPE,
    COMPARE_YOUR_COMPANY_TO,
    ANALYSIS_TYPE,
    REPORTING_GROUP,
    POLICY_DIVISION_ELIGIBLE_COVERAGE_TYPES,
    DASHBOARD_MESSAGES,
} from "../../constants";
import ReusableBarChart from "./charts/BarChart";
import ReusableDonutChart from "./charts/pieChart";
import FooterNote from "../claimleaveview/FooterNote";
import ChartLoader from "./chartLoader";
import moment from "moment";
import { useLazyGetDisabilityComparisonsQuery } from "../../slices/dashboardApiSlice";
import RequestFailed from "../../components/alerts/RequestFailed";
import { useFlags } from "launchdarkly-react-client-sdk";
import GTM from "../../GTM";
import useExportGraphsToPptx from "../../hooks/useExportGraphsToPptx";

const MIN_SIC_GROUPING_LEVEL = 2; // Major Group
const MAX_SIC_GROUPING_LEVEL = 4;
const DEFAULT_SIC_GROUPING_LEVEL = 2;

function TrendsAndBenchmarking(props) {
    const childRef = useRef(null);
    const chartLayoutRef = useRef(null);
    const [useWideLayout, setUseWideLayout] = useState(false);
    const { orgid } = useParams();
    const { user } = useSelector((state) => state.auth);
    const { config } = useSelector((state) => state.app);
    const dispatch = useDispatch();
    const configuredGroupingLevel = config?.REACT_APP_SIC_GROUPING_LEVEL;
    const sicGroupingLevel = useMemo(() => {
        const parsedLevel = Number(configuredGroupingLevel);
        if (
            !Number.isNaN(parsedLevel) &&
            parsedLevel >= MIN_SIC_GROUPING_LEVEL &&
            parsedLevel <= MAX_SIC_GROUPING_LEVEL
        ) {
            return parsedLevel;
        }

        return DEFAULT_SIC_GROUPING_LEVEL;
    }, [configuredGroupingLevel]);

    const [applyFilter, setApplyFilter] = useState(false);
    const {
        selectedOrganizationsReports,
        selectedOrganizationsIdReports: reportId,
        selectedRelatedOrganizationsForReports,
        selectedDataViewType: dataViewType,
        portalUserData,
    } = useSelector((state) => state.reportOrganization);

    const { snapshotReportDetails } = useSelector(
        (state) => state.disabilitySnapshotData
    );

    const { organizations: allOrganizations } = useSelector(
        (state) => state.userDetails
    );

    const { displayLeaveOptionTrendsBenchmarking } = useFlags();

    const allPolicies = useMemo(
        () =>
            portalUserData.assignedOrgs?.flatMap(
                (org) => org.assignedPolicies || []
            ) || [],
        [portalUserData.assignedOrgs]
    );

    const sicCodes = useMemo(() => {
        const truncatedCodes = allPolicies
            .map((policy) => {
                const codeValue = policy?.sicCode;
                if (codeValue == null) {
                    return "";
                }
                const normalized = `${codeValue}`.trim();
                if (!normalized) {
                    return "";
                }
                return normalized.slice(
                    0,
                    Math.min(sicGroupingLevel, normalized.length)
                );
            })
            .filter((code) => code !== "");

        return [...new Set(truncatedCodes)];
    }, [allPolicies, sicGroupingLevel]);

    const { exportGraphs, isExporting } = useExportGraphsToPptx();

    const {
        relatedOrganizationNames,
        organizationName,
        selectedIsfOrganizationIds,
    } = useMemo(
        () =>
            getSelectedIsfOrganizationIds(
                selectedOrganizationsReports,
                reportId,
                selectedRelatedOrganizationsForReports
            ),
        [
            selectedOrganizationsReports,
            reportId,
            selectedRelatedOrganizationsForReports,
        ]
    );

    const [resetFilters, setResetFilters] = useState(false);

    const selectedReportDetails = useMemo(() => {
        if (!snapshotReportDetails || snapshotReportDetails.length === 0)
            return {};
        return (
            snapshotReportDetails.find(
                (obj) => obj.DataViewType === "TrendsBenchmarking"
            ) || {}
        );
    }, [snapshotReportDetails]);

    const { ParametersOptions: allParametersOptions = [] } =
        selectedReportDetails;

    const isLeaveCoverageTypeEnabled = useMemo(
        () =>
            isUserEnabledForFlag(
                displayLeaveOptionTrendsBenchmarking,
                user,
                allOrganizations
            ),
        [displayLeaveOptionTrendsBenchmarking, user, allOrganizations]
    );

    const reportParametersOptions = useMemo(
        () =>
            filterLeaveCoverageTypeOption(
                allParametersOptions,
                isLeaveCoverageTypeEnabled
            ),
        [allParametersOptions, isLeaveCoverageTypeEnabled]
    );

    const enableCoverageType = useMemo(
        () => isParameterEnabled(reportParametersOptions, CONVERGE_TYPE),
        [reportParametersOptions]
    );

    const enableCompareYourCompanyTo = useMemo(
        () =>
            isParameterEnabled(
                reportParametersOptions,
                COMPARE_YOUR_COMPANY_TO
            ),
        [reportParametersOptions]
    );
    const enableAnalysisType = useMemo(
        () => isParameterEnabled(reportParametersOptions, ANALYSIS_TYPE),
        [reportParametersOptions]
    );
    const enableReportingGroup = useMemo(
        () => isParameterEnabled(reportParametersOptions, REPORTING_GROUP),
        [reportParametersOptions]
    );
    const { allowedPoliciesAndDivisions, allowedReportingGroups } = useMemo(
        () =>
            getAllowedPoliciesAndReportingGroups(
                portalUserData,
                selectedIsfOrganizationIds
            ),
        [portalUserData, selectedIsfOrganizationIds]
    );

    const initialPolicyDivision = useMemo(
        () =>
            allowedPoliciesAndDivisions.flatMap((item) => [
                item.policyId,
                ...(Array.isArray(item.divisions)
                    ? item.divisions.map(
                          (division) =>
                              `${item.policyId}-${division.divisionId}`
                      )
                    : []),
            ]),
        [allowedPoliciesAndDivisions]
    );

    const [selectedCompanyYourCompanyTo, setSelectedCompanyYourCompanyTo] =
        useState([]);
    const [selectedCoverageType, setSelectedCoverageType] = useState([]);
    const [selectedAnalysisType, setSelectedAnalysisType] = useState([]);
    const [selectedReportingGroup, setSelectedReportingGroup] = useState([]);
    const [selectedPolicyDivision, setSelectedPolicyDivision] = useState([]);

    useEffect(() => {
        setSelectedPolicyDivision(initialPolicyDivision);
    }, [initialPolicyDivision]);

    const [selectedReportingPeriod, setSelectedReportingPeriod] = useState({
        name: "",
        displayName: "",
        startYear: "",
        startQuarter: "",
        endYear: "",
        endQuarter: "",
    });
    const [dashboardCharts, setDashboardCharts] = useState([]);
    const [showChartResults, setShowChartResults] = useState(true);
    const handleFiltersChange = useCallback(() => {
        setShowChartResults(false);
    }, []);
    const [
        triggerComparisons,
        {
            data: DashboardData,
            isFetching: isComparisonsLoading,
            error: comparisonsError,
        },
    ] = useLazyGetDisabilityComparisonsQuery();

    const handleApplyFilter = useCallback(() => {
        const coverageTypesParam = [
            ...new Set(
                selectedCoverageType
                    .flatMap((coverage) => coverage.value)
                    .flatMap((value) => value.split(","))
            ),
        ];
        const compareTypesParam = selectedCompanyYourCompanyTo.map(
            (c) => c.value
        );

        const groupByParam =
            selectedAnalysisType[0]?.Value ?? selectedAnalysisType[0]?.value;
        const isPolicyDivisionEligibleCoverageSelected =
            coverageTypesParam.some((coverage) =>
                POLICY_DIVISION_ELIGIBLE_COVERAGE_TYPES.includes(coverage)
            );
        const policyDivisionsParam = isPolicyDivisionEligibleCoverageSelected
            ? selectedPolicyDivision.filter((item) => /^\d+-\d+$/.test(item))
            : [];
        const isTrendAnalysis = ["year", "quarter"].includes(groupByParam);
        const isLeaveCoverage = coverageTypesParam.includes("Leave");
        setShowChartResults(true);
        triggerComparisons({
            organizationIds: selectedIsfOrganizationIds,
            sicCodes,
            coverageTypes: coverageTypesParam,
            startYear: selectedReportingPeriod.startYear,
            startQuarter: selectedReportingPeriod.startQuarter,
            endYear: selectedReportingPeriod.endYear,
            endQuarter: selectedReportingPeriod.endQuarter,
            ...(!isTrendAnalysis && { compareTypes: compareTypesParam }),
            groupBy: groupByParam,
            ...(isLeaveCoverage
                ? {
                      reportingGroups: selectedReportingGroup.map(
                          (group) => group.value
                      ),
                  }
                : { policyDivisions: policyDivisionsParam }),
        });
    }, [
        selectedCoverageType,
        selectedCompanyYourCompanyTo,
        selectedAnalysisType,
        selectedPolicyDivision,
        selectedReportingGroup,
        selectedReportingPeriod,
        selectedIsfOrganizationIds,
        sicCodes,
        triggerComparisons,
    ]);

    useEffect(() => {
        if (!applyFilter) return;
        setApplyFilter(false);
        handleApplyFilter();
    }, [applyFilter, handleApplyFilter]);

    useEffect(() => {
        if (!DashboardData?.formattedCharts) return;
        setDashboardCharts(DashboardData.formattedCharts);
    }, [DashboardData]);

    useEffect(() => {
        const element = chartLayoutRef.current;
        if (!element) return;
        const updateLayout = () => {
            const standardWidth = window.innerWidth >= 1400 ? 1200 : 1024;
            const standardPlotWidth = standardWidth - 72;
            const needsMoreWidth = [
                ...element.querySelectorAll("[data-chart-min-width]"),
            ].some(
                (chart) =>
                    Number(chart.dataset.chartMinWidth) > standardPlotWidth
            );
            setUseWideLayout(window.innerWidth >= 1200 && needsMoreWidth);
        };
        const observer = new MutationObserver(updateLayout);
        observer.observe(element, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["data-chart-min-width"],
        });
        window.addEventListener("resize", updateLayout);
        updateLayout();
        return () => {
            observer.disconnect();
            window.removeEventListener("resize", updateLayout);
        };
    }, []);

    const currentDate = useMemo(
        () => moment().format("MM/DD/YYYY hh:mm A"),
        []
    );

    const allChartsHaveError = useMemo(
        () =>
            dashboardCharts.length > 0 &&
            dashboardCharts.every((chart) => chart.error),
        [dashboardCharts]
    );

    let previousUrls = props.previousUrl;
    let page_referrer = previousUrls[previousUrls.length - 1];
    const gtm = new GTM(null);

    const page_referrer_url =
        page_referrer === "/login"
            ? `${config.REACT_APP_ISERVICES}:81/LanAuthentication`
            : `${config.REACT_APP_BASE_URL}${page_referrer}`;

    const page_referrer_title = getPageReferrerTitle(
        page_referrer_url,
        user,
        dispatch
    );

    useEffect(() => {
        const { userId, isUserInternal } = user;
        const userEmail = user.userEmail || "";
        const reportsEnabled = true;

        gtm.PageView(
            "trends and benchmarking",
            organizationName,
            userId || "",
            isUserInternal ? "internal" : "external",
            // userRole,
            window.location.href,
            `${config.REACT_APP_BASE_URL}${page_referrer}`,
            page_referrer_title,
            orgid,
            userEmail,
            relatedOrganizationNames,
            selectedIsfOrganizationIds,
            reportsEnabled
        );
    }, []);

    const handleExport = useCallback(() => {
        exportGraphs({
            currentDate,
            filters: {
                organizationName,
                relatedOrganizationNames,
                selectedCompanyYourCompanyTo,
                selectedAnalysisType,
                selectedReportingGroup,
                selectedCoverageType,
                selectedReportingPeriod,
                selectedPolicyDivision,
            },
        });
    }, [
        currentDate,
        organizationName,
        exportGraphs,
        relatedOrganizationNames,
        selectedCompanyYourCompanyTo,
        selectedAnalysisType,
        selectedReportingGroup,
        selectedCoverageType,
        selectedReportingPeriod,
        selectedPolicyDivision,
    ]);

    const selectedCoverageTypeValue = useMemo(() => {
        if (
            Array.isArray(selectedCoverageType) &&
            selectedCoverageType.length > 0
        ) {
            return selectedCoverageType[0].value;
        }

        return "";
    }, [selectedCoverageType]);

    const renderCharts = useMemo(() => {
        if (allChartsHaveError) {
            return (
                <div className="d-flex no-data-message">
                    <div className="d-flex" style={{ gap: "8px" }}>
                        <i className="d-flex align-items-center fa-solid fa-circle-exclamation warning-color"></i>
                        <div
                            className="infobanner-text"
                            dangerouslySetInnerHTML={{
                                __html: DASHBOARD_MESSAGES.ALL_CHARTS_INSUFFICIENT_DATA,
                            }}
                        ></div>
                    </div>
                </div>
            );
        }

        return (
            <>
                <div
                    style={{
                        textAlign: "right",
                        marginBottom: 16,
                        color: "#037cb7",
                    }}
                >
                    <i className="fa-solid fa-download"></i>
                    <span
                        onClick={isExporting ? undefined : handleExport}
                        className="export-to-excel"
                    >
                        {isExporting ? "Exporting..." : "Export Dashboard"}
                    </span>
                </div>

                {dashboardCharts?.map((chart) => {
                    if (chart.variant === "bar") {
                        return (
                            <div
                                key={chart.id}
                                className={chart.error ? "" : "export-chart"}
                            >
                                <ReusableBarChart
                                    organizationName={organizationName}
                                    chartId={chart.id}
                                    title={chart.title}
                                    description={chart.description}
                                    accessibilityText={chart.accessibilityText}
                                    data={chart.data}
                                    groupBy={chart.groupBy}
                                    temporalLabels={chart.temporalLabels}
                                    axisLabelOrientation={
                                        chart.axisLabelOrientation
                                    }
                                    indexBy={chart.indexBy}
                                    variant={chart.variant}
                                    selectedCompanyYourCompanyTo={
                                        selectedCompanyYourCompanyTo
                                    }
                                    selectedFilters={{
                                        selectedReportingGroup,
                                        selectedPolicyDivision,
                                        selectedCoverageType:
                                            selectedCoverageTypeValue,
                                    }}
                                    layout={chart.layout}
                                    chartValueOrientation={
                                        chart.chartValueOrientation
                                    }
                                    disabilitySnapshot={false}
                                    chartValueType={chart.chartValueType}
                                    errorMessage={
                                        chart.error
                                            ? chart.errorMessage
                                            : undefined
                                    }
                                    clientClaimCount={chart.clientClaimCount}
                                />
                            </div>
                        );
                    } else if (chart.variant === "donut") {
                        return (
                            <div
                                key={chart.id}
                                className={chart.error ? "" : "export-chart"}
                            >
                                <ReusableDonutChart
                                    organizationName={organizationName}
                                    chartId={chart.id}
                                    title={chart.title}
                                    description={chart.description}
                                    accessibilityText={chart.accessibilityText}
                                    data={chart.data}
                                    groupBy={chart.groupBy}
                                    comparisons={chart.comparisons}
                                    layout={chart.layout}
                                    totals={chart.totals}
                                    labels={chart.labels}
                                    selectedCompanyYourCompanyTo={
                                        selectedCompanyYourCompanyTo
                                    }
                                    selectedFilters={{
                                        selectedReportingGroup,
                                        selectedPolicyDivision,
                                        selectedCoverageType:
                                            selectedCoverageTypeValue,
                                    }}
                                    disabilitySnapshot={false}
                                    errorMessage={
                                        chart.error
                                            ? chart.errorMessage
                                            : undefined
                                    }
                                    clientClaimCount={chart.clientClaimCount}
                                />
                            </div>
                        );
                    }

                    return null;
                })}

                <FooterNote date={currentDate} ref={childRef} />
            </>
        );
    }, [
        allChartsHaveError,
        dashboardCharts,
        organizationName,
        selectedCompanyYourCompanyTo,
        currentDate,
        handleExport,
        isExporting,
        selectedCoverageTypeValue,
        selectedReportingGroup,
    ]);

    return (
        <>
            <Breadcrumb
                links={[
                    { path: "/dashboard", name: "Dashboards" },
                    {
                        path: "/dashboard/trendsAndBenchmarking",
                        name: dataViewType,
                    },
                ]}
            />

            <div
                ref={chartLayoutRef}
                className={`trends-layout${useWideLayout ? " trends-layout--wide" : ""}`}
            >
                <VizFilters
                    organizationName={organizationName}
                    relatedOrganizationNames={relatedOrganizationNames}
                    userSelectedOrganizationReports={
                        selectedOrganizationsReports
                    }
                    reportId={reportId}
                    enableCoverageType={enableCoverageType}
                    dataViewType={"DCR"}
                    enableCompareYourCompanyTo={enableCompareYourCompanyTo}
                    enableAnalysisType={enableAnalysisType}
                    enableReportingGroup={enableReportingGroup}
                    reportingGroupOptions={allowedReportingGroups}
                    selectedCompanyYourCompanyTo={selectedCompanyYourCompanyTo}
                    selectedAnalysisType={selectedAnalysisType}
                    selectedReportingGroup={selectedReportingGroup}
                    initialCompareYourCompanyto={[]}
                    initialAnalysisTypeFilter={[]}
                    initialReportingGroup={[]}
                    initialCoverageFilter={[]}
                    resetFilters={resetFilters}
                    setResetFilters={setResetFilters}
                    setSelectedCoverageType={setSelectedCoverageType}
                    setSelectedCompanyYourCompanyTo={
                        setSelectedCompanyYourCompanyTo
                    }
                    setSelectedAnalysisType={setSelectedAnalysisType}
                    setSelectedReportingGroup={setSelectedReportingGroup}
                    selectedCoverageType={selectedCoverageType}
                    reportParametersOptions={reportParametersOptions}
                    setSelectedReportingPeriod={setSelectedReportingPeriod}
                    selectedReportingPeriod={selectedReportingPeriod}
                    setApplyFilter={setApplyFilter}
                    applyFilter={applyFilter}
                    enablePolicyandDivisisonFilter
                    allowedPoliciesAndDivisions={allowedPoliciesAndDivisions}
                    selectedPolicyDivision={selectedPolicyDivision}
                    setSelectedPolicyDivision={setSelectedPolicyDivision}
                    initialPolicyDivision={initialPolicyDivision}
                    onFiltersChange={handleFiltersChange}
                />

                {!showChartResults ? (
                    <ChartLoader loadingViewData={false} />
                ) : comparisonsError ? null : !isComparisonsLoading &&
                  DashboardData?.formattedCharts?.length > 0 ? (
                    renderCharts
                ) : (
                    <ChartLoader loadingViewData={isComparisonsLoading} />
                )}
            </div>

            {showChartResults && comparisonsError && (
                <RequestFailed
                    tabIndex={0}
                    message={DASHBOARD_MESSAGES.REQUEST_FAILED}
                    label="Dashboard Comparisons"
                    attribute1="dashboard_comparisons"
                    attribute3="Dashboard Comparisons Error"
                />
            )}
        </>
    );
}

export default TrendsAndBenchmarking;
